import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { QrCard } from '@/components/synapse/QrCard';
import {
  buildPatientPortalUrl,
  patientPortalQrFileBase,
} from '@/features/patients/portalLink';
import type { Patient } from '@/types/patients';

interface Props {
  patient: Pick<Patient, 'mrn' | 'portalToken'>;
  /** Hide the label and show only the icon — used in the cramped mobile header. */
  iconOnly?: boolean;
}

/**
 * Hands out the patient's own portal link, as a QR to scan or a URL to copy.
 *
 * Rendered from both places staff look at one patient — the registry detail
 * pane and the doctor's chart header — rather than each growing its own copy
 * button, so there is one link format and one warning.
 *
 * Renders NOTHING when `portalToken` is absent. That is not defensive coding:
 * list projections null the token on purpose (it is a bearer credential and a
 * search must not return forty working links), so a caller that passes a search
 * result gets no button instead of a link to `/p/patient/null`.
 */
export function PatientPortalLinkButton({ patient, iconOnly }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!patient.portalToken) return null;

  const url = buildPatientPortalUrl(window.location.origin, patient.portalToken);
  const fileBase = patientPortalQrFileBase(patient.mrn);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        {...(iconOnly ? { 'aria-label': t('patientPortal.linkButton') } : {})}
      >
        <Icon name="qr" size={15} />
        {!iconOnly && t('patientPortal.linkButton')}
      </Button>

      {open && (
        <Modal
          title={t('patientPortal.linkModalTitle')}
          description={t('patientPortal.linkModalDesc')}
          onClose={() => setOpen(false)}
          footer={
            <Button variant="secondary" full onClick={() => setOpen(false)}>
              {t('common.close')}
            </Button>
          }
        >
          <div className="flex flex-col gap-4">
            {/* Above the QR, not below it. The staff member handing this over is
                the only access control the link has — there is no patient login
                yet — so the warning has to be read before the copy button is,
                not after. */}
            <p className="flex items-start gap-2 rounded-md border border-line bg-paper-2 px-3.5 py-3 text-[12.5px] leading-snug text-muted">
              <Icon name="alert" size={16} className="mt-px shrink-0 text-warn" />
              {t('patientPortal.linkWarning')}
            </p>

            <QrCard
              url={url}
              fileBase={fileBase}
              title={t('patientPortal.linkCardTitle')}
              ariaLabel={t('patientPortal.linkAriaLabel')}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
